import { useMutation, useQuery } from "@apollo/client";
import {
  Button,
  Card,
  Drawer,
  Group,
  LoadingOverlay,
  Modal,
  ScrollArea,
  Select,
  Switch,
  useMantineTheme,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import EditIcon from "@mui/icons-material/Edit";
import {
  DELETE_LOAN_PRODUCT,
  TOGGLE_LOAN_PRODUCT_STATUS,
} from "apollo/mutuations";
import {
  ADMIN_LOAN_CATEGORIES,
  ADMIN_LOAN_PRODUCTS,
  LOAN_PROVIDERS,
} from "apollo/queries";
import ActionButton from "components/controls/ActionButtonNew";
import LoanProductAddModal from "components/LoanProduct/LoanProductAddModal";
import LoanProductEditModal from "components/LoanProduct/LoanProductEditModal";
import B2bTable from "components/reusable/b2bTable";
import { customLoader } from "components/utilities/loader";
import { useEffect, useState } from "react";
import { Trash } from "tabler-icons-react";
import { rolePermissions } from "utiles/permissions";
import { formatNumber } from "utiles/url";

const LoanProducts = () => {
  const [size, setSize] = useState("10");
  const [opened, setOpened] = useState(false);
  const [openedDelete, setOpenedDelete] = useState(false);
  const [openedEdit, setOpenedEdit] = useState(false);
  const [editId, setEditId] = useState();
  const [deleteID, setDeleteID] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [activePage, setActivePage] = useState(1);
  const [total, setTotal] = useState(0);
  const [providerFilter, setProviderFilter] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState(null);
  const roles = JSON.parse(localStorage.getItem("roles")) || [];
  const readOnly =
    rolePermissions.hasMaker(roles) || rolePermissions.hasChecker(roles);

  const { data, loading, refetch } = useQuery(ADMIN_LOAN_PRODUCTS, {
    variables: {
      first: parseInt(size),
      page: activePage,
      search: searchValue,
      provider_id: providerFilter,
      category_id: categoryFilter,
      orderBy: [
        {
          column: "NAME",
          order: "ASC",
        },
      ],
    },
    onError(error) {
      showNotification({
        color: "red",
        title: "Error",
        message:
          error?.graphQLErrors?.[0]?.message || "Failed to Get Loan Products",
      });
    },
  });

  // Query for providers
  const { data: providersData } = useQuery(LOAN_PROVIDERS, {
    variables: {
      first: 100,
      page: 1,
      orderBy: [{ column: "name", order: "ASC" }],
    },
  });

  // Query for categories
  const { data: categoriesData } = useQuery(ADMIN_LOAN_CATEGORIES, {
    variables: {
      first: 100,
      page: 1,
      orderBy: [{ column: "name", order: "ASC" }],
    },
  });

  const handlePageSizeChange = (newSize) => {
    setSize(newSize);
    setActivePage(1);
  };

  useEffect(() => {
    if (data) {
      setTotal(data.loanProducts.paginatorInfo.lastPage);
    }
  }, [data, size]);

  useEffect(() => {
    refetch();
  }, []);

  const [delLoanProduct] = useMutation(DELETE_LOAN_PRODUCT, {
    update(cache, { data: { deleteLoanProduct } }) {
      cache.updateQuery(
        {
          query: ADMIN_LOAN_PRODUCTS,
          variables: {
            first: parseInt(size),
            page: activePage,
            search: searchValue,
            orderBy: [
              {
                column: "NAME",
                order: "ASC",
              },
            ],
          },
        },
        (data) => {
          if (data.loanProducts.data.length === 1) {
            setTotal(total - 1);
            setActivePage(activePage - 1);
          } else {
            return {
              loanProducts: {
                data: [
                  ...data.loanProducts.data.filter(
                    (product) => product.id !== deleteLoanProduct.id,
                  ),
                ],
              },
            };
          }
        },
      );
    },
  });

  const [toggleProductStatus] = useMutation(TOGGLE_LOAN_PRODUCT_STATUS, {
    onCompleted(data) {
      showNotification({
        color: "green",
        title: "Success",
        message: "Product Status Updated Successfully",
      });
      refetch();
    },
    onError(error) {
      showNotification({
        color: "red",
        title: "Error",
        message:
          error?.graphQLErrors?.[0]?.message || "Failed to Update Status",
      });
    },
  });

  const theme = useMantineTheme();

  const handleToggleStatus = (productId) => {
    toggleProductStatus({
      variables: { product_id: productId },
    });
  };

  const headerData = [
    {
      label: "#",
      key: "index",
      sortable: false,
      searchable: false,
      width: "3%",
      render: (_rowData, rowIndex) => {
        const idx = (activePage - 1) * parseInt(size || "0") + (rowIndex + 1);
        return <span>{idx}</span>;
      },
    },
    {
      label: "Name",
      key: "name",
      width: "15%",
      sortable: true,
      searchable: true,
      render: (rowData) => {
        return <span>{rowData.name}</span>;
      },
    },
    {
      label: "Provider",
      key: "provider",
      sortable: false,
      searchable: true,
      width: "10%",
      render: (rowData) => {
        return <span>{rowData.provider?.name || "N/A"}</span>;
      },
    },
    {
      label: "Category",
      key: "category",
      sortable: false,
      searchable: true,
      width: "10%",
      render: (rowData) => {
        return <span>{rowData.category?.name || "N/A"}</span>;
      },
    },
    {
      label: "Interest",
      key: "interest_rate",
      sortable: true,
      searchable: false,
      width: "8%",
      render: (rowData) => {
        return (
          <span>
            {rowData.interest_rate}% ({rowData.interest_type})
          </span>
        );
      },
    },
    {
      label: "Amount Range",
      key: "amount_range",
      sortable: false,
      searchable: false,
      width: "12%",
      render: (rowData) => {
        return (
          <span>
            {formatNumber(rowData.min_amount)} -{" "}
            {formatNumber(rowData.max_amount)}
          </span>
        );
      },
    },
    {
      label: "Term (Months)",
      key: "term",
      sortable: false,
      searchable: false,
      width: "8%",
      render: (rowData) => {
        return (
          <span>
            {rowData.min_term} - {rowData.max_term}
          </span>
        );
      },
    },
    {
      label: "Processing Fee",
      key: "processing_fee",
      sortable: false,
      searchable: false,
      width: "10%",
      render: (rowData) => {
        return (
          <span>
            {rowData.processing_fee}
            {rowData.processing_fee_type === "PERCENT" ? "%" : ""}
          </span>
        );
      },
    },
    {
      label: "Status",
      key: "status",
      sortable: false,
      searchable: false,
      width: "8%",
      render: (rowData) => {
        return (
          <Switch
            checked={rowData.status === "ACTIVE" || rowData.status === 1}
            onChange={() => {
              if (!readOnly) {
                handleToggleStatus(rowData.id);
              }
            }}
            color="green"
            size="md"
            disabled={readOnly}
          />
        );
      },
    },
    {
      label: "Actions",
      key: "actions",
      sortable: false,
      searchable: false,
      width: "16%",
      render: (rowData) => {
        if (readOnly) {
          return null;
        }
        return (
          <div style={{ display: "flex", gap: "6px" }}>
            <ActionButton
              label="Edit"
              onClick={() => handleEditLoanProduct(rowData)}
            >
              <EditIcon style={{ fontSize: "1rem" }} />
            </ActionButton>
            <ActionButton
              label="Delete"
              color="error"
              onClick={() => handleDelete(`${rowData.id}`)}
            >
              <Trash size={15} />
            </ActionButton>
          </div>
        );
      },
    },
  ];

  const optionsData = readOnly
    ? undefined
    : {
        actionLabel: "Add Loan Product",
        setAddModal: setOpened,
      };

  const handleDelete = (id) => {
    setOpenedDelete(true);
    setDeleteID(id);
  };

  const handleEditLoanProduct = (product) => {
    setOpenedEdit(true);
    setEditId(product);
  };

  const handleChange = (currentPage) => {
    setActivePage(currentPage);
  };

  const deleteLoanProduct = () => {
    delLoanProduct({
      variables: { id: deleteID },
      onCompleted(data) {
        setOpenedDelete(false);
        setDeleteID(null);
        showNotification({
          color: "green",
          title: "Success",
          message: "Loan Product Deleted Successfully",
        });
      },
      onError(error) {
        setOpenedDelete(false);
        setDeleteID(null);
        showNotification({
          color: "red",
          title: "Error",
          message:
            error?.graphQLErrors?.[0]?.message ||
            "Failed to Delete Loan Product",
        });
      },
    });
  };

  const [confirmedSearch, setConfirmedSearch] = useState("");

  const handleManualSearch = (searchTerm) => {
    setSearchValue(searchTerm);
  };

  const clearInput = () => {
    setSearchValue("");
    setConfirmedSearch("");
  };

  return loading ? (
    <LoadingOverlay
      visible={loading}
      color="blue"
      overlayBlur={2}
      loader={customLoader}
    />
  ) : (
    <div style={{ width: "98%", margin: "auto" }}>
      {!readOnly && (
        <Drawer
          opened={opened}
          overlayColor={
            theme.colorScheme === "dark"
              ? theme.colors.dark[9]
              : theme.colors.gray[2]
          }
          overlayOpacity={0.55}
          overlayBlur={3}
          padding="xl"
          onClose={() => setOpened(false)}
          position="bottom"
          size="85%"
          styles={{
            closeButton: {
              color: "black",
              marginTop: "10px",
            },
          }}
        >
          <LoanProductAddModal
            total={total}
            setTotal={setTotal}
            activePage={activePage}
            setActivePage={setActivePage}
            setOpened={setOpened}
            size={size}
            refetch={refetch}
          />
        </Drawer>
      )}

      {!readOnly && (
        <Drawer
          opened={openedEdit}
          overlayColor={
            theme.colorScheme === "dark"
              ? theme.colors.dark[9]
              : theme.colors.gray[2]
          }
          overlayOpacity={0.55}
          overlayBlur={3}
          styles={{
            closeButton: {
              color: "black",
              marginTop: "10px",
            },
          }}
          padding="xl"
          onClose={() => setOpenedEdit(false)}
          position="bottom"
          size="85%"
        >
          <LoanProductEditModal
            setOpenedEdit={setOpenedEdit}
            editData={editId}
            data={data ? data.loanProducts.data : []}
          />
        </Drawer>
      )}

      {!readOnly && (
        <Modal
          opened={openedDelete}
          onClose={() => setOpenedDelete(false)}
          title="Warning"
          centered
        >
          <p>Are you sure you want to delete this loan product?</p>
          <Group position="right">
            <Button onClick={() => deleteLoanProduct()} color="red">
              Delete
            </Button>
          </Group>
        </Modal>
      )}

      {/* Filters Section */}
      <Card shadow="sm" p="md" mb="md">
        <Group spacing="md" align="flex-end">
          <Select
            label="Loan Provider"
            placeholder="All Providers"
            clearable
            value={providerFilter}
            onChange={(value) => {
              setProviderFilter(value);
              setActivePage(1);
            }}
            data={
              providersData?.loanProviders?.data?.map((provider) => ({
                value: provider.id,
                label: `${provider.name} (${provider.code})`,
              })) || []
            }
            style={{ minWidth: 250 }}
            withinPortal
            searchable
          />
          <Select
            label="Loan Category"
            placeholder="All Categories"
            clearable
            value={categoryFilter}
            onChange={(value) => {
              setCategoryFilter(value);
              setActivePage(1);
            }}
            data={
              categoriesData?.loanCategories?.data?.map((category) => ({
                value: category.id,
                label: category.name,
              })) || []
            }
            style={{ minWidth: 200 }}
            withinPortal
            searchable
          />
        </Group>
      </Card>

      <Card shadow="sm" p="lg">
        <ScrollArea>
          <B2bTable
            total={total}
            activePage={activePage}
            handleChange={handleChange}
            header={headerData}
            clearInput={clearInput}
            paginatorInfo={data?.loanProducts.paginatorInfo}
            handelSearch={handleManualSearch}
            searchValue={confirmedSearch}
            onSearchChange={setConfirmedSearch}
            optionsData={optionsData}
            loading={loading}
            data={data ? data.loanProducts.data : []}
            size={size}
            handlePageSizeChange={handlePageSizeChange}
          />
        </ScrollArea>
      </Card>
    </div>
  );
};

export default LoanProducts;
